import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowRight,
  Bookmark,
  BookOpen,
  Compass,
  Library,
  Loader2,
  PenLine,
  Plus
} from 'lucide-react'
import PillButton from './PillButton'
import { Heading } from '@astryxdesign/core/Heading'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Text } from '@astryxdesign/core/Text'
import BookCover from './BookCover'
import LandingHeader from './LandingHeader'
import PublishedStoryCard from './PublishedStoryCard'
import { publishedStoryUrl } from './publishedStoryLinks'
import SettingsPanel, { type SettingsSection } from './SettingsPanel'
import StreakCalendar from './StreakCalendar'
import { authClient } from '../lib/auth-client'
import { getRecentlyReadStoryIds } from '../lib/recentlyRead'
import { startWriting } from '../lib/startWriting'
import { addLocalDays, formatLocalDate, type WritingDay } from '../lib/writingDays'
import type { AppSettings } from '../App'

interface ContinueDraft {
  metadata: StoryMetadata
}

function draftUrl(storyId: string): string {
  return `/drafts/${encodeURIComponent(storyId)}/canvas`
}

function formatLastEdited(timestamp: number): string {
  const elapsedMs = Math.max(0, Date.now() - timestamp)
  const elapsedDays = elapsedMs / 86_400_000

  if (elapsedDays < 30) {
    const relativeTime = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
    if (elapsedMs < 60_000) return relativeTime.format(0, 'second')
    if (elapsedMs < 3_600_000) return relativeTime.format(-Math.floor(elapsedMs / 60_000), 'minute')
    if (elapsedMs < 86_400_000)
      return relativeTime.format(-Math.floor(elapsedMs / 3_600_000), 'hour')
    if (elapsedDays < 7) return relativeTime.format(-Math.floor(elapsedDays), 'day')
    return relativeTime.format(-Math.floor(elapsedDays / 7), 'week')
  }

  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: new Date(timestamp).getFullYear() === new Date().getFullYear() ? undefined : 'numeric'
  })
}

function applyTheme(theme: AppSettings['theme']): void {
  const resolvedTheme =
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : theme
  const root = document.documentElement
  root.setAttribute('data-theme', resolvedTheme)
  root.classList.toggle('dark', resolvedTheme === 'dark')
  root.style.colorScheme = resolvedTheme
}

export default function HomeView(): React.JSX.Element {
  const { data: session, isPending } = authClient.useSession()
  const [publishedStories, setPublishedStories] = useState<PublishedStorySummary[]>([])
  const [drafts, setDrafts] = useState<StoryMetadata[]>([])
  const [continueDrafts, setContinueDrafts] = useState<ContinueDraft[]>([])
  const [authorProfile, setAuthorProfile] = useState<AuthorProfile | null>(null)
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [writingDays, setWritingDays] = useState<WritingDay[]>([])
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('account')
  const [showSettings, setShowSettings] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isPending) return
    if (!session?.user) {
      window.location.replace('/')
      return
    }
    let active = true
    const today = formatLocalDate(new Date())
    Promise.all([
      window.api.publishedStories.list(),
      window.api.stories.list(),
      window.api.profiles.getActive(),
      window.api.settings.get(),
      // A year of history is what the "longest streak" figure is computed from; the calendar
      // itself only renders the current month.
      window.api.writingDays.list(addLocalDays(today, -365), today).catch(() => [])
    ])
      .then(([published, storyDrafts, profile, loadedSettings, days]) => {
        const orderedDrafts = [...storyDrafts].sort((a, b) => b.updatedAt - a.updatedAt)
        const detailedDrafts = orderedDrafts.slice(0, 3).map((metadata) => ({ metadata }))
        if (!active) return
        setPublishedStories(published)
        setDrafts(orderedDrafts)
        setContinueDrafts(detailedDrafts)
        setAuthorProfile(profile)
        setSettings(loadedSettings)
        setWritingDays(days)
        applyTheme(loadedSettings.theme)
      })
      .catch((reason) => {
        if (active)
          setError(reason instanceof Error ? reason.message : 'Unable to load your home feed.')
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => {
      active = false
    }
  }, [isPending, session?.user])

  const recentlyRead = useMemo(() => {
    const ids = getRecentlyReadStoryIds()
    const storiesById = new Map(publishedStories.map((story) => [story.id, story]))
    return ids.flatMap((id) => {
      const story = storiesById.get(id)
      return story ? [story] : []
    })
  }, [publishedStories])

  const writingHref = drafts[0] ? draftUrl(drafts[0].id) : undefined

  // A ref rather than the isCreating state, because repeated clicks arrive in the same tick:
  // React has not re-rendered between them, so each one reads the same stale `false` and mints
  // its own story. A ref updates synchronously, so only the first click gets through.
  const creationInFlight = useRef(false)

  const createStory = async (): Promise<void> => {
    if (creationInFlight.current) return
    creationInFlight.current = true
    setIsCreating(true)
    try {
      const story = await window.api.stories.create('Untitled Story')
      window.location.href = draftUrl(story.id)
    } catch (reason) {
      creationInFlight.current = false
      setError(reason instanceof Error ? reason.message : 'Unable to start a new story.')
      setIsCreating(false)
    }
  }

  // "Write" resumes the newest draft. A writer who has none gets a new story instead of being
  // bounced to a list page they cannot start from.
  //
  // When `drafts` is empty it delegates rather than creating directly, because an empty list here
  // does not mean the writer has no stories: the feed load is all-or-nothing, so a failed request
  // and a still-in-flight one both look identical to a genuinely empty account. The shared helper
  // re-fetches and decides, so a writer with twenty drafts never gets a junk story minted for them.
  const handleWrite = (): void => {
    if (writingHref) {
      window.location.href = writingHref
      return
    }
    if (creationInFlight.current) return
    creationInFlight.current = true
    setIsCreating(true)
    startWriting().catch((reason) => {
      creationInFlight.current = false
      setError(reason instanceof Error ? reason.message : 'Unable to start writing.')
      setIsCreating(false)
    })
  }

  const openSettings = (section: 'account' | 'profile'): void => {
    setSettingsSection(section)
    void window.api.settings
      .get()
      .then((latestSettings) => {
        setSettings(latestSettings)
        setShowSettings(true)
      })
      .catch(() => setShowSettings(true))
  }

  const saveSettings = (updatedSettings: AppSettings): void => {
    setSettings(updatedSettings)
    localStorage.setItem('ninenights-settings', JSON.stringify(updatedSettings))
    void window.api.settings.save(updatedSettings)
  }

  const syncTheme = (theme: AppSettings['theme']): void => {
    setSettings((current) => (current ? { ...current, theme } : current))
  }

  const saveProfile = async (changes: {
    penName: string
    authorBlurb: string
    libraryPublic: boolean
  }): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.update(authorProfile.id, changes)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const uploadAvatar = async (file: File): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.uploadAvatar(authorProfile.id, file)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const removeAvatar = async (): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.removeAvatar(authorProfile.id)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const selectDefaultAvatar = async (defaultAvatarId: string): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.selectDefaultAvatar(
      authorProfile.id,
      defaultAvatarId
    )
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  if (isPending || !session?.user) {
    return (
      <main className="home-loading-state">
        <Loader2 className="animate-spin" aria-label="Loading" />
      </main>
    )
  }

  const firstName = session.user.name?.trim().split(/\s+/)[0] || 'writer'
  const accountDisplayName = authorProfile?.penName.trim() || session.user.name?.trim() || firstName

  return (
    <main className="home-shell">
      <LandingHeader
        showNavLinks={false}
        showAppButton={false}
        isAppHeader
        isAccountLoading={isLoading}
        onWrite={handleWrite}
        userName={accountDisplayName}
        avatarUrl={authorProfile?.avatarUrl}
        onOpenSettings={openSettings}
        currentTheme={settings?.theme}
        onThemeChange={syncTheme}
      />
      <section className="home-page-frame">
        <aside className="home-menu" aria-label="Home navigation">
          <VStack gap={8} justify="between" className="home-menu-inner">
            <VStack gap={6}>
              {/* A plain button wearing the nav item's class rather than the Button
                  component: matching the nav items meant overriding almost everything
                  Button brings, and the two would have drifted apart on the next change. */}
              <button
                type="button"
                className="home-menu-item home-new-story"
                onClick={() => void createStory()}
                disabled={isCreating}
              >
                <Plus size={19} /> New story
              </button>
              <nav>
                <VStack gap={1}>
                  {/* A button, not a link: Write has no one stable destination — it may have to
                      create a story first — so there is no honest href to put on it. */}
                  <button
                    type="button"
                    className="home-menu-item home-menu-action"
                    onClick={handleWrite}
                    disabled={isCreating}
                  >
                    <PenLine size={19} /> Write
                  </button>
                  <a href="/home" className="home-menu-item home-menu-item-active">
                    <Compass size={19} /> Discover
                  </a>
                  <a href="/my-stories" className="home-menu-item">
                    <Library size={19} /> My stories
                  </a>
                  <span className="home-menu-divider" aria-hidden="true" />
                  <a href="/library" className="home-menu-item">
                    <Bookmark size={19} /> Library
                  </a>
                </VStack>
              </nav>
            </VStack>
            <footer className="home-menu-legal">
              <VStack gap={2}>
                <HStack gap={3} wrap="wrap">
                  <a href="/terms" className="home-legal-link">
                    Terms
                  </a>
                  <a href="/privacy" className="home-legal-link">
                    Privacy
                  </a>
                </HStack>
                <Text type="supporting">
                  © 2026 9nights.
                  <br />
                  All rights reserved.
                </Text>
              </VStack>
            </footer>
          </VStack>
        </aside>

        {/* The middle and right columns share one scroll box so they travel together while the
            nav rail stays put. */}
        <section className="home-scroll-area">
          <section className="home-main-column">
            <section className="home-writing-banner">
              <Text type="label" className="home-eyebrow">
                Welcome back, {firstName}
              </Text>
              <VStack gap={4} className="home-hero-copy">
                <Heading level={1} type="display-2">
                  Where will your story
                  <br />
                  take you tonight?
                </Heading>
                <Text color="secondary">
                  Pick up where you left off or start something entirely new.
                  <br />
                  Your next chapter is waiting.
                </Text>
                <HStack gap={3} wrap="wrap">
                  <PillButton
                    label="Continue writing"
                    icon={<PenLine size={16} />}
                    onClick={handleWrite}
                    isDisabled={isCreating}
                  />
                  <PillButton
                    variant="secondary"
                    label="New story"
                    icon={<Plus size={16} />}
                    onClick={() => void createStory()}
                    isDisabled={isCreating}
                  />
                </HStack>
              </VStack>
              <span className="home-hero-nine" aria-hidden="true">
                9
              </span>
            </section>

            <section className="home-discover-section">
              <HStack justify="between" align="end" gap={4}>
                <VStack gap={0}>
                  <Heading level={2}>Stories worth discovering</Heading>
                  <Text type="supporting">Freshly published by the 9nights community.</Text>
                </VStack>
                <a href="/home" className="home-view-all">
                  View all stories <ArrowRight size={16} />
                </a>
              </HStack>

              {isLoading ? (
                <section className="home-empty-feed">
                  <Text type="supporting">Loading stories…</Text>
                </section>
              ) : error ? (
                <section className="home-empty-feed">
                  <Text color="secondary">{error}</Text>
                </section>
              ) : publishedStories.length === 0 ? (
                <section className="home-empty-feed">
                  <VStack gap={3} align="center">
                    <BookOpen size={28} />
                    <Heading level={3}>The first page is yours.</Heading>
                    <Text type="supporting">
                      Publish a story from the editor and it will appear here.
                    </Text>
                    <PillButton
                      label="Go write something"
                      onClick={handleWrite}
                      isDisabled={isCreating}
                    />
                  </VStack>
                </section>
              ) : (
                <section className="home-feed-list">
                  {publishedStories.slice(0, 6).map((story, index) => (
                    <PublishedStoryCard key={story.id} story={story} index={index} />
                  ))}
                </section>
              )}
            </section>
          </section>

          <aside className="home-right-column">
            <StreakCalendar days={writingDays} />
            <section className="home-side-card">
              <HStack justify="between" align="center">
                <Heading level={3}>My Recent Writings</Heading>
                <a href="/my-stories" className="home-view-all">
                  View all <ArrowRight size={16} />
                </a>
              </HStack>
              {continueDrafts.length === 0 ? (
                <Text type="supporting">Your latest manuscripts will appear here.</Text>
              ) : (
                <VStack gap={0}>
                  {continueDrafts.map((draft) => (
                    <a
                      key={draft.metadata.id}
                      href={draftUrl(draft.metadata.id)}
                      className="home-continue-row"
                    >
                      <VStack gap={1} className="home-side-story-copy">
                        <Text type="label" maxLines={1}>
                          {draft.metadata.name}
                        </Text>
                        <Text type="supporting">
                          Last edited {formatLastEdited(draft.metadata.updatedAt)}
                        </Text>
                      </VStack>
                    </a>
                  ))}
                </VStack>
              )}
            </section>

            <section className="home-side-card">
              <HStack justify="between" align="center">
                <Heading level={3}>Recently read</Heading>
                <a href="/home" className="home-view-all">
                  View all <ArrowRight size={16} />
                </a>
              </HStack>
              {recentlyRead.length === 0 ? (
                <Text type="supporting">
                  Stories you open will collect here for an easy return.
                </Text>
              ) : (
                <VStack gap={0}>
                  {recentlyRead.slice(0, 4).map((story, index) => (
                    <a key={story.id} href={publishedStoryUrl(story)} className="home-recent-story">
                      <HStack gap={4} align="center">
                        <BookCover
                          size="sm"
                          coverUrl={story.coverUrl}
                          title={story.title}
                          authorName={story.authorName}
                          placeholderIndex={index}
                          className="home-story-cover-recent"
                        />
                        <VStack gap={1}>
                          <Text type="label" maxLines={1}>
                            {story.title}
                          </Text>
                          <Text type="supporting">{story.authorName}</Text>
                          <Text type="supporting">{story.readingTimeMinutes} min read</Text>
                        </VStack>
                      </HStack>
                    </a>
                  ))}
                </VStack>
              )}
            </section>
          </aside>
        </section>
      </section>
      {showSettings && settings && (
        <SettingsPanel
          settings={settings}
          profile={authorProfile}
          initialSection={settingsSection}
          onSaveSettings={saveSettings}
          onSaveProfile={saveProfile}
          onUploadAvatar={uploadAvatar}
          onRemoveAvatar={removeAvatar}
          onSelectDefaultAvatar={selectDefaultAvatar}
          onClose={() => setShowSettings(false)}
        />
      )}
    </main>
  )
}
